import { z } from "zod";
import { idSchema, idempotencyKeySchema, instantSchema } from "./common.js";

/**
 * N-01 schemas for the new-panel capture flow. Three concerns:
 *
 * 1. `PhotoRole` — the canonical role tag a photo carries (mirrors
 *    the domain `PhotoRole` union). Used by the `photos.updateRole`
 *    procedure when the post-capture review chip is picked.
 * 2. `panels.analyze*` — the async-job surface around the
 *    synchronous `ai.scanPanel` call. Lets the client show stepwise
 *    progress, support cancellation, and survive backgrounding.
 * 3. `panelModels.search` — searchable catalog of known panel
 *    models for the "Pick different model" UI on the confirm screen.
 *
 * The procedures themselves are wired in `apps/api` (deferred); this
 * file defines the input + output Zod shapes the client will type
 * against.
 */

// --- photo roles ---------------------------------------------------

/**
 * Mirrors `PhotoRole` in `@panelgenie/domain`. The union is closed
 * because consumers branch on every arm (the AI scan input, the
 * legend renderer, the apply-panel-scan dispatcher); a missing arm
 * is a compile-time error at every site.
 */
export const photoRoleSchema = z.enum([
  "deadFrontOn",
  "deadFrontOff",
  "breakerCloseUp",
  "nameplate",
  "legendCard",
  "busExposed",
  "finding",
  "other",
]);
export type PhotoRoleSchema = z.infer<typeof photoRoleSchema>;

/**
 * `photos.updateRole` — re-tag a photo's role after the user picked
 * a different chip on the post-capture review screen. Also accepts
 * an optional note (tablet form factor inlines a note field; phone
 * defers to the gallery).
 *
 * Idempotent on `photoId`: the same call from a retry returns the
 * same response.
 */
export const photosUpdateRoleInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  photoId: idSchema,
  role: photoRoleSchema,
  note: z.string().max(2000).optional(),
});
export type PhotosUpdateRoleInput = z.infer<typeof photosUpdateRoleInputSchema>;

export const photosUpdateRoleOutputSchema = z.object({
  photoId: idSchema,
  role: photoRoleSchema,
});
export type PhotosUpdateRoleOutput = z.infer<
  typeof photosUpdateRoleOutputSchema
>;

// --- analyze job ---------------------------------------------------

/**
 * Discriminated union mirroring `AnalyzeJobStatus` in `@panelgenie/domain`.
 * Non-terminal arms (`queued` → `readingLabels`) are pure phase
 * markers, except `uploading` which carries a `[0, 1]` byte-fraction.
 * Terminal arms carry their disposition.
 */
export const analyzeJobStatusSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("queued") }),
  z.object({
    kind: z.literal("uploading"),
    progress: z.number().min(0).max(1),
  }),
  z.object({ kind: z.literal("detectingPanel") }),
  z.object({ kind: z.literal("identifyingBreakers") }),
  z.object({ kind: z.literal("readingLabels") }),
  z.object({
    kind: z.literal("succeeded"),
    suggestionSetId: idSchema,
  }),
  z.object({
    kind: z.literal("failed"),
    reason: z.string().min(1).max(2000),
  }),
  z.object({ kind: z.literal("cancelled") }),
]);
export type AnalyzeJobStatusSchema = z.infer<typeof analyzeJobStatusSchema>;

/**
 * `panels.analyze` — kicks off an `AnalyzeJob` against the supplied
 * photos. Returns a `jobId` the client polls (`analyzeStatus`) or
 * cancels (`cancelAnalyze`). The photo count and per-photo size caps
 * are enforced by the existing `photos.uploadUrl` flow that produces
 * these ids; this procedure only checks non-empty.
 *
 * Mutating, so carries an `idempotencyKey`: a retry returns the same
 * `jobId` rather than starting a second invocation. Matches the AI
 * scan cost model — re-runs of the same submission are user-initiated
 * (the mockup's "scan again") and go through a different code path.
 */
export const panelsAnalyzeInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  panelId: idSchema,
  photoIds: z.array(idSchema).min(1).max(8),
});
export type PanelsAnalyzeInput = z.infer<typeof panelsAnalyzeInputSchema>;

export const panelsAnalyzeOutputSchema = z.object({
  jobId: idSchema,
});
export type PanelsAnalyzeOutput = z.infer<typeof panelsAnalyzeOutputSchema>;

/**
 * `panels.analyzeStatus` — read-only poll of an `AnalyzeJob`. Returns
 * the full job value so the client can render the active step row,
 * surface upload progress, and detect terminal disposition in one
 * call. Polled at ~1s while the user is on the analyzing screen.
 */
export const panelsAnalyzeStatusInputSchema = z.object({
  jobId: idSchema,
});
export type PanelsAnalyzeStatusInput = z.infer<
  typeof panelsAnalyzeStatusInputSchema
>;

export const panelsAnalyzeStatusOutputSchema = z.object({
  id: idSchema,
  panelId: idSchema,
  photoIds: z.array(idSchema).min(1),
  status: analyzeJobStatusSchema,
  startedAt: instantSchema,
  finishedAt: instantSchema.nullable(),
});
export type PanelsAnalyzeStatusOutput = z.infer<
  typeof panelsAnalyzeStatusOutputSchema
>;

/**
 * `panels.cancelAnalyze` — the user tapped Cancel on the analyzing
 * screen. The server transitions the job to `cancelled` and aborts
 * the underlying provider call when possible (best-effort — some
 * providers don't expose cancellation, in which case the result is
 * discarded on receipt).
 *
 * Idempotent: cancelling an already-terminal job is a no-op success.
 */
export const panelsCancelAnalyzeInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  jobId: idSchema,
});
export type PanelsCancelAnalyzeInput = z.infer<
  typeof panelsCancelAnalyzeInputSchema
>;

export const panelsCancelAnalyzeOutputSchema = z.object({
  jobId: idSchema,
  status: analyzeJobStatusSchema,
});
export type PanelsCancelAnalyzeOutput = z.infer<
  typeof panelsCancelAnalyzeOutputSchema
>;

// --- panel model catalog -------------------------------------------

/**
 * One `PanelModelSummary` on the wire. Mirrors the domain type from
 * `@panelgenie/domain` exactly; the server returns these from
 * `panelModels.search` and the client's "Pick different model"
 * bottom-sheet renders them.
 */
export const panelModelSummarySchema = z.object({
  id: idSchema,
  manufacturer: z.string().min(1),
  series: z.string().min(1),
  model: z.string().min(1),
  totalSlots: z.number().int().positive().max(200),
  defaultMainAmperage: z.number().int().positive(),
  defaultNumberingScheme: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("sequential-down-then-over") }),
    z.object({ kind: z.literal("odd-left-even-right") }),
    z.object({ kind: z.literal("sequential-left-then-down") }),
  ]),
  dangerous: z.boolean(),
});
export type PanelModelSummarySchema = z.infer<typeof panelModelSummarySchema>;

/**
 * `panelModels.search` — free-text search against the catalog.
 * Empty / whitespace queries return the catalog's natural order so
 * the UI can render the full list before any input. `limit` caps the
 * result set; defaults to 50 server-side.
 */
export const panelModelsSearchInputSchema = z.object({
  q: z.string().max(200).default(""),
  limit: z.number().int().positive().max(100).default(50),
});
export type PanelModelsSearchInput = z.infer<
  typeof panelModelsSearchInputSchema
>;

export const panelModelsSearchOutputSchema = z.object({
  results: z.array(panelModelSummarySchema),
});
export type PanelModelsSearchOutput = z.infer<
  typeof panelModelsSearchOutputSchema
>;
